import type {
  CampaignDoc,
  ChannelDocument,
  ContentPageDoc,
  HardwareProductDoc,
  IndexPageDoc,
  MyPageBundleConfigDoc,
  MyPageProductBundleDoc,
  ProductDocument,
  RegradeDocument,
  SectionPageDoc,
  SoftwareProductDoc,
} from 'modules/cms/types';
import { cmsService } from './bootstrap';

export const getDocumentsByType = (
  documentType: 'channel' | 'product',
): Promise<Array<ChannelDocument | ProductDocument>> => cmsService.getDocumentsByType(documentType);

export const getSoftwareProducts = (): Promise<SoftwareProductDoc[]> => {
  const query = `*[_type == 'softwareProduct']{
       ..., 
        commercialProductIds, 
        upgrade->{ ..., _id },
        downgrade->{ ..., _id },
        "regradeStandard": select(
          count(regradeStandard) > 0 => regradeStandard[]->{ 
            ..., 
            _id,
            "notAvailableIfHasProduct": notAvailableIfHasProduct[]->commercialProductId,
            "availableOnlyIfHasProduct": availableOnlyIfHasProduct[]->commercialProductId
          },
          defined(regradeStandard._ref) => [regradeStandard->{ 
            ..., 
            _id,
            "notAvailableIfHasProduct": notAvailableIfHasProduct[]->commercialProductId,
            "availableOnlyIfHasProduct": availableOnlyIfHasProduct[]->commercialProductId
          }],
          null
        ),
        "regradeWithContractPeriod": select(
          count(regradeWithContractPeriod) > 0 => regradeWithContractPeriod[]->{ 
            ..., 
            _id,
            "notAvailableIfHasProduct": notAvailableIfHasProduct[]->commercialProductId,
            "availableOnlyIfHasProduct": availableOnlyIfHasProduct[]->commercialProductId
          },
          defined(regradeWithContractPeriod._ref) => [regradeWithContractPeriod->{ 
            ..., 
            _id,
            "notAvailableIfHasProduct": notAvailableIfHasProduct[]->commercialProductId,
            "availableOnlyIfHasProduct": availableOnlyIfHasProduct[]->commercialProductId
          }],
          null
        ),
        churnProfiling->{
                        name,
                        externalName,
                        active,
                        description, 
                        "targets": procedure.targets[]->{
                          ...,
                          "image": coalesce(image.asset->url, ""),
                          commercialProducts[]{
                            commercialProduct->{commercialProductId, name, productType},
                         	offer->{name, offerId}
                          },
                        }},
        "image": coalesce(image.asset->url, ""), 
        "logo": coalesce(logo.asset->url, "")
    }`;
  return cmsService.getDocumentsByQuery(query);
};

export const getHardwareProducts = (): Promise<HardwareProductDoc[]> => {
  const query = `*[_type == "hardwareProduct"]{
        _createdAt,
        _id,
        _rev,
        _type,
        _updatedAt,
        commercialProductId,
        name,
        model,
        "category": *[_type == "hardwareCategory" && slug.current == ^.category][0].title,
        "image": coalesce(image.asset->url, ""),
    }`;
  return cmsService.getDocumentsByQuery(query);
};

export const getPages = (): Promise<Array<SectionPageDoc | IndexPageDoc | ContentPageDoc>> => {
  const query = `*[_type in ["sectionPage", "indexPage", "contentPage"]]{
        ..., 
        'parent': parent->_id,
        children[]->{ _id, name, 'icon': icon.asset->url, url },
        sections[] {
          _key,
          title,
          'image': image.asset->url,
          'imageClass': image.className,
          content,
          items[] {
            _key,
            '_id': link->_id,
            description,
            title,
            button,
            'icon': image.asset->url,
            'url': link->url
          }
        },
        nodes[] { _key, title, content, 'image': image.asset->url, link },
        "image": coalesce(image.asset->url, ""), 
        'public': true,
        "icon": coalesce(icon.asset->url, "") }`;
  return cmsService.getDocumentsByQuery(query);
};

export const getCampaignPages = (): Promise<CampaignDoc[]> => {
  const query = `*[_type in ["mypageCampaign"]] {
        ...,
        "image": coalesce(image.asset->url, ""),
        'products': commercialProducts[] { 
            'commercialProductId': commercialProduct->commercialProductId,
            'productOfferId': offer->offerId,
            'type': commercialProduct->_type
        },
        startDate,
        endDate,
        'public': false,
        contractPeriod
    }`;
  return cmsService.getDocumentsByQuery(query);
};

export const getRegradeProcedures = (): Promise<RegradeDocument[]> => {
  const query = `*[_type in ["regrade"]] {
        _id,
        _type,
        procedure,
        description
}`;
  return cmsService.getDocumentsByQuery(query);
};

export const getProductBundles = (): Promise<MyPageProductBundleDoc[]> => {
  const query = `*[_type == "mypageProductBundle"]{
        _id,
        _type,
        name,
        tag,
        footer,
        notes,
        more,
        description,
        contractPeriod,
        onlyForBusinessUnits,
        notForBusinessUnits,
        onlyForCustomerTypes,
        notForCustomerTypes,
        priceOverride,
        "image": coalesce(image.asset->url, ""),
        imageType,
        commercialProducts[] { commercialProduct->{_id, 'type': _type, name, commercialProductId, alacarte, productType }, offer-> { _id, name, offerId } },
        conflictingProductsWithOffers[] { commercialProduct->{_id, name, commercialProductId }, offer-> { _id, name, offerId }},
        'group': group->{ _id, name } 
    }`;
  return cmsService.getDocumentsByQuery(query);
};

export const getBundleConfigs = (): Promise<MyPageBundleConfigDoc[]> => {
  const query = `*[_type == "mypageBundleConfig"] {
        _id,
        _type,
        listId,
        items
    }`;
  return cmsService.getDocumentsByQuery(query);
};
